import { numFont } from '../../utils/fonts';
import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  InputAdornment,
  Paper,
  Snackbar,
  Tab,
  Tabs,
  TextField,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import SearchIcon from '@mui/icons-material/Search';
import MergeIcon from '@mui/icons-material/Merge';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import BusinessIcon from '@mui/icons-material/Business';
import PersonIcon from '@mui/icons-material/Person';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import DataTable from '../shared/DataTable';
import PaginationControls from '../shared/PaginationControls';
import CustomerAutocomplete from '../shared/CustomerAutocomplete';
import NewCustomerForm, { EMPTY_NEW_CUSTOMER, isNewCustomerValid } from '../shared/NewCustomerForm';
import KoontilaskuDialog from './KoontilaskuDialog';
import TargetLabelField from './TargetLabelField';
import PartsSaleStatusBadge from './PartsSaleStatusBadge';
import { formatDate } from '../../utils';
import { formatPriceWithCurrency } from '../../utils/vat';
import { partsSaleErrorMessage } from './salesUtils';

/**
 * Lähetteet — delivery-note tabs for billing customers, per the osamyynti
 * design: status tabs with counts, search + customer filter, rich rows, and
 * multi-select consolidation (koontilasku) locked to one customer at a time.
 */
function PartsSalesDeliveriesView() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [tab, setTab] = useState('open');
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [customer, setCustomer] = useState(null);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [counts, setCounts] = useState({ open: null, invoiced: null, pickup: null });
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [totalCount, setTotalCount] = useState(0);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [newOpen, setNewOpen] = useState(false);
  const [newCustomer, setNewCustomer] = useState(null);
  const [newTarget, setNewTarget] = useState('');
  // 0 = pick an existing customer, 1 = register a new one (company by default: lähetteet are for billing customers)
  const [newTab, setNewTab] = useState(0);
  const [newCustomerDraft, setNewCustomerDraft] = useState({ ...EMPTY_NEW_CUSTOMER, type: 'company' });
  const [creating, setCreating] = useState(false);
  const [consolidateOpen, setConsolidateOpen] = useState(false);
  const [snack, setSnack] = useState({ open: false, message: '', severity: 'success' });

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search), 300);
    return () => clearTimeout(timer);
  }, [search]);

  const fetchCounts = useCallback(async () => {
    try {
      const [open, invoiced, pickup] = await Promise.all([
        axios.get('/api/parts-sales/?mode=deferred&status=open&page_size=1'),
        axios.get('/api/parts-sales/?mode=deferred&status=invoiced&page_size=1'),
        axios.get('/api/parts-sales/?mode=deferred&special=arrived&page_size=1'),
      ]);
      setCounts({
        open: open.data.count ?? 0,
        invoiced: invoiced.data.count ?? 0,
        pickup: pickup.data.count ?? 0,
      });
    } catch (e) {
      /* badge counts are cosmetic */
    }
  }, []);

  const fetchRows = useCallback(async () => {
    setLoading(true);
    try {
      let url = `/api/parts-sales/?mode=deferred&page=${page}&page_size=${pageSize}`;
      if (tab === 'pickup') url += '&special=arrived';
      else if (tab !== 'all') url += `&status=${tab}`;
      if (customer?.id) url += `&customer_id=${customer.id}`;
      if (debouncedSearch) {
        // A digit-only query is a lähete number — exact filter, not text search.
        const numeric = /^#?\d+$/.test(debouncedSearch.trim());
        url += numeric
          ? `&number=${debouncedSearch.trim().replace('#', '')}`
          : `&search=${encodeURIComponent(debouncedSearch)}`;
      }
      const resp = await axios.get(url);
      setRows(resp.data.results || resp.data);
      setTotalCount(resp.data.count ?? (resp.data.results || resp.data).length);
      setSelectedIds(new Set());
    } catch (e) {
      setSnack({ open: true, message: t('parts_sales.loadFailed'), severity: 'error' });
    } finally {
      setLoading(false);
    }
  }, [tab, customer, debouncedSearch, page, pageSize, t]);

  useEffect(() => {
    fetchRows();
  }, [fetchRows]);

  useEffect(() => {
    fetchCounts();
  }, [fetchCounts]);

  useEffect(() => {
    setPage(1);
  }, [tab, customer, debouncedSearch]);

  const openNewDialog = () => {
    setNewTab(0);
    setNewCustomer(null);
    setNewTarget('');
    setNewCustomerDraft({ ...EMPTY_NEW_CUSTOMER, type: 'company' });
    setNewOpen(true);
  };

  const canCreate = newTab === 0 ? !!newCustomer : isNewCustomerValid(newCustomerDraft, { requireName: true });

  const createLahete = async () => {
    if (!canCreate) return;
    setCreating(true);
    let customerId = newCustomer?.id;
    if (newTab === 1) {
      // Register the customer first (same shared form + YTJ/e-invoice lookups as
      // the other dialogs), then open the lähete for it.
      try {
        const created = await axios.post('/api/customers/', newCustomerDraft);
        customerId = created.data.id;
      } catch (e) {
        setSnack({ open: true, message: t('Failed to create customer'), severity: 'error' });
        setCreating(false);
        return;
      }
    }
    try {
      const resp = await axios.post('/api/parts-sales/', {
        customer_id: customerId,
        ...(newTarget.trim() ? { target_label: newTarget.trim() } : {}),
      });
      setNewOpen(false);
      setNewCustomer(null);
      setNewTarget('');
      navigate(`/lahetteet/${resp.data.id}`);
    } catch (e) {
      setSnack({ open: true, message: partsSaleErrorMessage(e, t, 'parts_sales.createFailed'), severity: 'error' });
    } finally {
      setCreating(false);
    }
  };

  const selectedRows = rows.filter((r) => selectedIds.has(r.id));
  const selectableRows = selectedRows.filter((r) => r.status === 'open');
  const selectedCustomerIds = new Set(selectableRows.map((r) => r.customer));
  const consolidatable =
    selectableRows.length > 0 &&
    selectableRows.length === selectedRows.length &&
    selectedCustomerIds.size === 1 &&
    !selectedCustomerIds.has(null);
  const selectionTotal = selectableRows.reduce((s, r) => s + parseFloat(r.total_excl_vat || 0), 0);
  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));

  const columns = [
    {
      key: 'number',
      label: t('parts_sales.number'),
      width: '1%',
      render: (r) => (
        <Typography variant="body2" sx={{ ...numFont, fontWeight: 600, whiteSpace: 'nowrap' }}>
          #{r.number}
        </Typography>
      ),
    },
    {
      key: 'customer_name',
      label: t('parts_sales.customer'),
      maxWidth: 260,
      render: (r) => (
        <Box>
          <Typography variant="body2" sx={{ fontWeight: 500 }} noWrap>
            {r.customer_type === 'company' && <BusinessIcon sx={{ fontSize: 14, mr: 0.5, verticalAlign: 'text-bottom', color: 'text.secondary' }} />}
            {r.customer_name || '—'}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {r.customer_type === 'company' ? t('parts_sales.billingCustomer') : t('parts_sales.privateCustomer')}
          </Typography>
        </Box>
      ),
    },
    {
      key: 'created_at',
      label: t('parts_sales.created'),
      width: '1%',
      render: (r) => (
        <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
          {formatDate(r.created_at)}
        </Typography>
      ),
    },
    {
      key: 'line_count',
      label: t('parts_sales.lines'),
      width: '1%',
      render: (r) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, whiteSpace: 'nowrap' }}>
          <Typography variant="body2">{r.line_count}</Typography>
          {r.ordered_count > 0 && (
            <Chip size="small" color="info" variant="outlined" label={t('parts_sales.orderedChip', { count: r.ordered_count })} sx={{ height: 18, fontSize: 10, fontWeight: 600 }} />
          )}
          {r.arrived_count > 0 && (
            <Chip size="small" color="success" label={t('parts_sales.arrivedChip', { count: r.arrived_count })} sx={{ height: 18, fontSize: 10, fontWeight: 600 }} />
          )}
          {r.has_free_text_lines && (
            <Chip size="small" variant="outlined" label={t('parts_sales.freeTag')} sx={{ height: 18, fontSize: 10 }} />
          )}
          {r.has_margin_lines && (
            <Chip size="small" variant="outlined" color="warning" label={t('parts_sales.marginTag')} sx={{ height: 18, fontSize: 10 }} />
          )}
        </Box>
      ),
    },
    {
      key: 'total_excl_vat',
      label: t('parts_sales.netTotal'),
      align: 'right',
      width: '1%',
      render: (r) => (
        <Typography variant="body2" sx={{ ...numFont, fontWeight: 600, whiteSpace: 'nowrap' }}>
          {formatPriceWithCurrency(parseFloat(r.total_excl_vat || 0))}
        </Typography>
      ),
    },
    {
      key: 'status',
      label: t('parts_sales.statusLabel'),
      width: '1%',
      render: (r) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, whiteSpace: 'nowrap' }}>
          <PartsSaleStatusBadge status={r.status} />
          {r.invoice_number && (
            <Typography variant="caption" color="text.secondary" sx={{ ...numFont }}>
              → {r.invoice_number}
            </Typography>
          )}
        </Box>
      ),
    },
  ];

  const tabLabel = (label, count) => (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
      {label}
      {count != null && (
        <Chip size="small" label={count} sx={{ height: 18, fontSize: 11, '& .MuiChip-label': { px: 0.75 } }} />
      )}
    </Box>
  );

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }} data-testid="deliveries-view">
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2, mb: 0.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <LocalShippingIcon color="secondary" />
          <Typography variant="h4" sx={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontWeight: 700 }}>
            {t('parts_sales.deliveries')}
          </Typography>
        </Box>
        <Button
          variant="contained"
          color="secondary"
          startIcon={<AddIcon />}
          onClick={openNewDialog}
          data-testid="new-lahete"
          sx={{ borderRadius: '8px' }}
        >
          {t('parts_sales.newDelivery')}
        </Button>
      </Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {t('parts_sales.deliveriesSubtitle')}
      </Typography>

      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2, mb: 2 }}>
        <Tabs
          value={tab}
          onChange={(e, v) => setTab(v)}
          variant="scrollable"
          scrollButtons="auto"
          allowScrollButtonsMobile
          sx={{ minHeight: 40, maxWidth: '100%', '& .MuiTab-root': { minHeight: 40, textTransform: 'none' } }}
        >
          <Tab value="open" label={tabLabel(t('parts_sales.tabOpen'), counts.open)} data-testid="lahete-tab-open" />
          <Tab value="pickup" label={tabLabel(t('parts_sales.tabPickup'), counts.pickup)} data-testid="lahete-tab-pickup" />
          <Tab value="invoiced" label={tabLabel(t('parts_sales.tabInvoiced'), counts.invoiced)} data-testid="lahete-tab-invoiced" />
          <Tab value="all" label={t('parts_sales.tabAll')} data-testid="lahete-tab-all" />
        </Tabs>
        <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center', flexWrap: 'wrap' }}>
          <TextField
            size="small"
            placeholder={t('parts_sales.searchPlaceholder')}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
            sx={{ width: 220 }}
          />
          <Box sx={{ width: 240 }}>
            <CustomerAutocomplete value={customer} onChange={setCustomer} placeholder={t('parts_sales.allCustomers')} />
          </Box>
        </Box>
      </Box>

      <DataTable
        columns={columns}
        data={rows}
        loading={loading}
        onRowClick={(r) => navigate(`/lahetteet/${r.id}`)}
        selectable={tab === 'open'}
        selectedIds={selectedIds}
        onSelectionChange={setSelectedIds}
        rowIdField="id"
        selectionActions={[
          {
            label:
              `${t('parts_sales.consolidateAction')}` +
              (consolidatable ? ` · ${formatPriceWithCurrency(selectionTotal)}` : ''),
            icon: <MergeIcon fontSize="small" />,
            onClick: () => setConsolidateOpen(true),
            color: 'secondary',
            variant: 'contained',
            disabled: !consolidatable,
            tooltip: consolidatable ? undefined : t('parts_sales.consolidateOneCustomer'),
          },
        ]}
        emptyIcon={<ReceiptLongIcon sx={{ fontSize: 40, color: 'text.disabled' }} />}
        emptyTitle={tab === 'open' ? t('parts_sales.noOpenDeliveries') : t('parts_sales.noDeliveries')}
        emptyDescription={t('parts_sales.deliveriesEmptyHint')}
        pagination={{
          page,
          totalPages,
          totalCount,
          pageSize,
          onPageChange: setPage,
          onPageSizeChange: (s) => {
            setPageSize(s);
            setPage(1);
          },
        }}
        testIdPrefix="lahete"
        tableTestId="lahetteet-table"
      />

      {/* Mobile Cards - hidden on desktop. Consolidation (multi-select) is a
          desktop-only tool, same as bulk actions on the invoice list. */}
      <Box sx={{ display: { xs: 'block', lg: 'none' }, position: 'relative' }}>
        {/* Loading overlay for refetch */}
        {loading && rows.length > 0 && (
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              bgcolor: 'rgba(255, 255, 255, 0.7)',
              zIndex: 1,
              display: 'flex',
              alignItems: 'flex-start',
              justifyContent: 'center',
              pt: 8,
            }}
          >
            <CircularProgress size={32} />
          </Box>
        )}
        {loading && rows.length === 0 ? (
          <Box sx={{ p: 6, display: 'flex', justifyContent: 'center' }}>
            <CircularProgress size={32} />
          </Box>
        ) : rows.length === 0 ? (
          <Paper elevation={0} sx={{ p: 4, textAlign: 'center' }}>
            <ReceiptLongIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
            <Typography color="text.secondary">
              {tab === 'open' ? t('parts_sales.noOpenDeliveries') : t('parts_sales.noDeliveries')}
            </Typography>
            <Typography variant="body2" color="text.disabled" sx={{ mt: 1 }}>
              {t('parts_sales.deliveriesEmptyHint')}
            </Typography>
          </Paper>
        ) : (
          <>
            {rows.map((r) => (
              <Paper
                key={r.id}
                data-testid={`lahete-card-${r.id}`}
                elevation={0}
                onClick={() => navigate(`/lahetteet/${r.id}`)}
                sx={{
                  p: 2,
                  mb: 1.5,
                  cursor: 'pointer',
                  transition: 'background-color 0.15s ease',
                  '&:hover': {
                    bgcolor: 'action.hover',
                  },
                }}
              >
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 1.5 }}>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5, flexWrap: 'wrap' }}>
                      <Typography sx={{ fontSize: '14px', fontWeight: 600, ...numFont }}>
                        #{r.number}
                      </Typography>
                      <PartsSaleStatusBadge status={r.status} />
                      {r.invoice_number && (
                        <Typography variant="caption" color="text.secondary" sx={{ ...numFont }}>
                          → {r.invoice_number}
                        </Typography>
                      )}
                    </Box>
                    <Typography sx={{ fontSize: '14px', fontWeight: 500, color: 'text.primary' }} noWrap>
                      {r.customer_type === 'company' && (
                        <BusinessIcon sx={{ fontSize: 14, mr: 0.5, verticalAlign: 'text-bottom', color: 'text.secondary' }} />
                      )}
                      {r.customer_name || '—'}
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mt: 0.5, flexWrap: 'wrap' }}>
                      <Typography variant="caption" color="text.secondary">
                        {formatDate(r.created_at)} · {t('parts_sales.lines')}: {r.line_count}
                      </Typography>
                      {r.ordered_count > 0 && (
                        <Chip size="small" color="info" variant="outlined" label={t('parts_sales.orderedChip', { count: r.ordered_count })} sx={{ height: 18, fontSize: 10, fontWeight: 600 }} />
                      )}
                      {r.arrived_count > 0 && (
                        <Chip size="small" color="success" label={t('parts_sales.arrivedChip', { count: r.arrived_count })} sx={{ height: 18, fontSize: 10, fontWeight: 600 }} />
                      )}
                    </Box>
                  </Box>
                  <Typography sx={{ fontSize: '15px', fontWeight: 600, ...numFont, whiteSpace: 'nowrap', flexShrink: 0 }}>
                    {formatPriceWithCurrency(parseFloat(r.total_excl_vat || 0))}
                  </Typography>
                </Box>
              </Paper>
            ))}

            {/* Mobile Pagination Controls */}
            <PaginationControls
              page={page}
              totalPages={totalPages}
              totalCount={totalCount}
              pageSize={pageSize}
              onPageChange={setPage}
              onPageSizeChange={(s) => {
                setPageSize(s);
                setPage(1);
              }}
              loading={loading}
            />
          </>
        )}
      </Box>

      {/* New lähete dialog */}
      <Dialog
        open={newOpen}
        onClose={() => !creating && setNewOpen(false)}
        maxWidth={newTab === 1 ? 'sm' : 'xs'}
        fullWidth
        PaperProps={{ sx: { borderRadius: '16px' } }}
      >
        <DialogTitle sx={{ pb: 0.5 }}>{t('parts_sales.newDelivery')}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            {t('parts_sales.newDeliveryHelp')}
          </Typography>
          <Tabs
            value={newTab}
            onChange={(e, v) => setNewTab(v)}
            variant="fullWidth"
            sx={{ mb: 2, borderBottom: 1, borderColor: 'divider' }}
          >
            <Tab icon={<PersonIcon />} iconPosition="start" label={t('Select existing')} data-testid="new-lahete-tab-existing" />
            <Tab icon={<AddIcon />} iconPosition="start" label={t('Create new')} data-testid="new-lahete-tab-new" />
          </Tabs>
          {newTab === 0 ? (
            <CustomerAutocomplete
              value={newCustomer}
              onChange={setNewCustomer}
              autoFocus
              placeholder={t('parts_sales.selectCustomer')}
            />
          ) : (
            <NewCustomerForm t={t} value={newCustomerDraft} onChange={setNewCustomerDraft} size="small" requireName />
          )}
          <TargetLabelField
            value={newTarget}
            onChange={setNewTarget}
            customerId={newTab === 0 ? newCustomer?.id : null}
            label={t('parts_sales.targetDefault')}
            placeholder={t('parts_sales.targetPlaceholder')}
            helperText={t('parts_sales.targetDefaultHelp')}
            inputTestId="new-lahete-target"
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setNewOpen(false)} disabled={creating}>{t('Cancel')}</Button>
          <Button
            variant="contained"
            color="secondary"
            startIcon={creating ? <CircularProgress size={16} color="inherit" /> : <AddIcon />}
            disabled={!canCreate || creating}
            onClick={createLahete}
            data-testid="new-lahete-create"
          >
            {t('parts_sales.create')}
          </Button>
        </DialogActions>
      </Dialog>

      <KoontilaskuDialog
        open={consolidateOpen}
        onClose={() => setConsolidateOpen(false)}
        saleIds={[...selectedIds]}
        onDone={(invoice) => {
          setConsolidateOpen(false);
          setSnack({
            open: true,
            message: t('parts_sales.koontilaskuCreated', { number: invoice?.invoice_number }),
            severity: 'success',
          });
          fetchRows();
          fetchCounts();
        }}
      />

      <Snackbar
        open={snack.open}
        autoHideDuration={5000}
        onClose={() => setSnack({ ...snack, open: false })}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert severity={snack.severity} onClose={() => setSnack({ ...snack, open: false })} sx={{ width: '100%' }}>
          {snack.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}

export default PartsSalesDeliveriesView;
